import { CSSProperties } from 'react'
import { GroupBase, StylesConfig } from 'react-select'

import { SelectOption } from '@/components/MrtTable'
import { colors } from '@/styles'
import * as theme from '@/styles'

export interface SelectConfigOptions {
	$compact?: boolean
	customStyles?: CSSProperties
	isClearable?: boolean
	isTableCell?: boolean
}

/**
 * Generate complete styles configuration for react-select
 * Merges all styling logic from StyledSelect into a single function
 */
export const getStylesConfig = <T extends SelectOption = SelectOption>(
	options?: SelectConfigOptions,
): StylesConfig<T, boolean, GroupBase<T>> => {
	const { customStyles, isClearable, $compact, isTableCell } = options || {}
	const { borderWidth, backgroundColor } = customStyles || {}

	return {
		menu: (baseStyles) => ({
			...baseStyles,
			zIndex: 10000,
		}),

		menuPortal: (baseStyles) => ({
			...baseStyles,
			zIndex: 10000,
		}),

		option: (baseStyles, state) => {
			const { base, light } = colors.primary

			return {
				...baseStyles,
				background: state.isSelected ? base : state.isFocused ? light : '#fff',
				':hover': {
					...baseStyles[':hover'],
					background: state.isSelected ? '#000' : light,
				},
				':active': {
					...baseStyles[':active'],
					background: state.isSelected ? '#000' : light,
				},
			}
		},

		indicatorSeparator: (baseStyles) => ({
			...baseStyles,
			...($compact && { width: 0 }),
			...(isTableCell && { display: 'none' }),
		}),

		control: (baseStyles, state) => ({
			...baseStyles,
			borderColor: $compact
				? `${theme.colors.input.$compact.horizontalBorder} ${theme.colors.input.$compact.verticalBorder}`
				: `${theme.colors.input.border}`,
			borderRadius: $compact ? 0 : `${theme.input.borderRadius}`,
			boxShadow:
				state.isFocused && !$compact
					? `0 0 0 1px ${theme.mainColors.primary}`
					: 'none',
			'&:hover': {
				boxShadow:
					state.isFocused && !$compact
						? `0 0 0 1px ${theme.mainColors.primary}`
						: 'none',
			},
			minHeight: $compact ? 'auto' : 'auto',
			height: '100%',
			borderWidth: borderWidth || '1px',
			background: backgroundColor || 'transparent',
			...(isTableCell && {
				border: 'none',
				boxShadow: 'none',
				background: 'transparent',
				minHeight: 'unset',
				'&:hover': { boxShadow: 'none' },
			}),
		}),

		placeholder: (baseStyles) => ({
			...baseStyles,
			color: `${theme.colors.input.placeholder}`,
		}),

		valueContainer: (baseStyles) => ({
			...baseStyles,
			marginRight: isClearable
				? $compact
					? '38px'
					: '55px'
				: $compact
					? '18px'
					: '26px',
			...($compact && {
				margin: '0 1px',
				padding: '1px 3px',
				top: 0,
				whiteSpace: 'nowrap',
				flexWrap: 'nowrap',
				overflow: 'hidden',
				textOverflow: 'ellipsis',
			}),
			...(isTableCell && {
				paddingTop: 0,
				paddingBottom: 0,
				paddingRight: 0,
				paddingLeft: 2,
				marginRight: 45,
				overflow: 'hidden',
			}),
		}),

		indicatorsContainer: (baseStyles) => ({
			...baseStyles,
			position: 'absolute',
			top: '50%',
			right: 0,
			transform: 'translate(0, -50%)',
			height: '100%',
			...(isTableCell && {
				flexShrink: 0,
				gap: 0,
			}),
		}),

		dropdownIndicator: (baseStyles) => ({
			...baseStyles,
			padding: $compact ? 0 : isTableCell ? 2 : '8px 4px',
		}),

		clearIndicator: (baseStyles) => ({
			...baseStyles,
			padding: $compact ? 0 : isTableCell ? 2 : '8px 4px',
			cursor: 'pointer',
			pointerEvents: 'auto',
		}),

		singleValue: (baseStyles, state) => ({
			...baseStyles,
			overflow: 'initial',
			color: state.isDisabled ? 'black' : baseStyles.color,
			...(isTableCell && {
				textOverflow: 'ellipsis',
				overflow: 'hidden',
				whiteSpace: 'nowrap',
			}),
		}),

		multiValue: (baseStyles) => ({
			...baseStyles,
			...($compact && {
				transition: '0.2s all',
				'&:hover': {
					flexShrink: 0,
				},
			}),
		}),

		container: (baseStyles) => ({
			...baseStyles,
			width: '100%',
			height: '100%',
		}),
	}
}
